import { useCallback, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { Button } from '@cbhq/cds-web/buttons';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { PageFooter } from '@cbhq/cds-web/page/PageFooter';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { UnsavedChangesAlert } from '../../../Settings/components/UnsavedChangesAlert';

const messages = defineMessages({
  cancel: {
    id: 'Security.approvalRulesSaveBar.cancel',
    defaultMessage: 'Cancel',
    description: 'Button label for discarding approval rule changes',
  },
  save: {
    id: 'Security.approvalRulesSaveBar.save',
    defaultMessage: 'Save',
    description: 'Button label for saving approval rule changes',
  },
});

type ApprovalRulesSaveBarProps = {
  hasChanges: boolean;
  saving: boolean;
  canSave: boolean;
  onCancel: () => void;
  onSave: () => void;
};

export function ApprovalRulesSaveBar({
  hasChanges,
  saving,
  canSave,
  onCancel,
  onSave,
}: ApprovalRulesSaveBarProps) {
  const { formatMessage } = useIntl();
  const [isDiscardAlertVisible, setIsDiscardAlertVisible] = useState(false);
  const saveBarStyle = useMemo<CSSProperties>(
    () => ({
      position: 'fixed',
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 1,
      transform: hasChanges ? 'translateY(0)' : 'translateY(100%)',
      opacity: hasChanges ? 1 : 0,
      pointerEvents: hasChanges ? 'auto' : 'none',
      transition: 'transform 220ms ease-out, opacity 220ms ease-out',
    }),
    [hasChanges],
  );

  const handleCancelClick = useCallback(() => {
    setIsDiscardAlertVisible(true);
  }, []);

  const handleKeepEditing = useCallback(() => {
    setIsDiscardAlertVisible(false);
  }, []);

  const handleDiscardChanges = useCallback(() => {
    setIsDiscardAlertVisible(false);
    onCancel();
  }, [onCancel]);

  return (
    <>
      <VStack background="bg" data-testid="approval-rules-save-bar" style={saveBarStyle}>
        <PageFooter
          borderedTop
          height="auto"
          action={
            <HStack alignItems="center" gap={2} justifyContent="flex-end">
              <Button
                onClick={handleCancelClick}
                compact
                variant="secondary"
                transparent
                disabled={!hasChanges || saving}
              >
                {formatMessage(messages.cancel)}
              </Button>
              <Button
                onClick={onSave}
                compact
                loading={saving}
                disabled={!hasChanges || !canSave || saving}
              >
                {formatMessage(messages.save)}
              </Button>
            </HStack>
          }
        />
      </VStack>
      <UnsavedChangesAlert
        visible={isDiscardAlertVisible}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscardChanges}
      />
    </>
  );
}
